<template>
    <div>
        <div v-if="showCoachTabs" class="flex gap-4 mb-8 justify-center">
            <div
                @click="activeCoachTab = 'main'"
                class="px-8 py-3 rounded-lg cursor-pointer font-semibold transition-colors"
                :class="activeCoachTab === 'main' ? 'bg-[#BD9135] text-black' : 'bg-[#474849] text-white hover:bg-[#5a5b5c]'"
            >
                Головний тренер
            </div>
            <div
                @click="activeCoachTab = 'goalkeeper'"
                class="px-8 py-3 rounded-lg cursor-pointer font-semibold transition-colors"
                :class="activeCoachTab === 'goalkeeper' ? 'bg-[#BD9135] text-black' : 'bg-[#474849] text-white hover:bg-[#5a5b5c]'"
            >
                Тренер воротарів
            </div>
        </div>

        <div class="grid grid-cols-5 gap-7">
            <div
                v-for="item in visibleTiles"
                :key="item.id"
                class="bg-[#BD9135] w-[250px] h-[250px] rounded-md cursor-pointer relative group"
                @click="onTileClick(item)"
            >
                <div
                    v-if="editable && canEdit"
                    class="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity z-10"
                    @click.stop
                >
                    <button
                        type="button"
                        class="bg-black/40 rounded p-1 hover:bg-black/60"
                        title="Перейменувати"
                        @click="startRename(item)"
                    >
                        <img src="@/assets/icons/pen_icon.svg" class="w-4 h-4" />
                    </button>
                    <button
                        type="button"
                        class="bg-black/40 rounded p-1 hover:bg-black/60"
                        title="Видалити"
                        @click="confirmDelete(item)"
                    >
                        <img src="@/assets/icons/trash_icon.svg" class="w-4 h-4" />
                    </button>
                </div>

                <div class="w-full h-[200px] flex justify-center items-center">
                    <div v-if="renamingId === item.id" class="px-4 w-full" @click.stop>
                        <input
                            v-model="renameValue"
                            class="w-full bg-white text-black rounded px-2 py-1 outline-none"
                            @keyup.enter="submitRename(item)"
                        />
                        <div class="flex justify-center gap-2 mt-2">
                            <button type="button" class="bg-black text-white text-xs px-2 py-1 rounded" @click="submitRename(item)">OK</button>
                            <button type="button" class="bg-white/70 text-black text-xs px-2 py-1 rounded" @click="cancelRename">×</button>
                        </div>
                    </div>
                    <img v-else src="@/assets/icons/trainingModule/Frame 427321054.svg" alt="" />
                </div>
                <div class="w-full h-[50px] text-center flex justify-center items-center">
                    <div class="text-black font-bold px-[20px] line-clamp-2">
                        {{ item.name }}
                    </div>
                </div>
            </div>

            <div
                v-if="editable && canEdit && !renamingId"
                class="border-2 border-dashed border-[#BD9135] w-[250px] h-[250px] rounded-md cursor-pointer flex flex-col justify-center items-center text-[#BD9135]"
                @click="showAddForm = true"
            >
                <div v-if="!showAddForm" class="text-center font-bold text-lg px-4">
                    + Додати папку
                </div>
                <div v-else class="px-4 w-full" @click.stop>
                    <input
                        v-model="newName"
                        class="w-full bg-transparent border border-[#BD9135] text-white rounded px-2 py-1 outline-none placeholder:text-white/50"
                        placeholder="Назва папки"
                        @keyup.enter="submitAdd"
                    />
                    <div class="flex justify-center gap-2 mt-3">
                        <button type="button" class="bg-[#BD9135] text-black text-sm font-semibold px-3 py-1 rounded" @click="submitAdd">Додати</button>
                        <button type="button" class="bg-[#474849] text-white text-sm px-3 py-1 rounded" @click="cancelAdd">Скасувати</button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { ref, computed, onMounted, watch } from 'vue'
import { uuid } from 'vue3-uuid'
import {
    getAllTrainingElementFoldersWithSubfolders,
    addTrainingElementFolder,
    updateTrainingElementFolderById,
    deleteTrainingElementFolderById
} from '@/services/apiRequests'

export default {
    name: 'TrainingElementFoldersGrid',

    props: {
        editable: {
            type: Boolean,
            default: false
        },
        showCoachTabs: {
            type: Boolean,
            default: true
        }
    },

    emits: ['select'],

    setup(props, { emit }) {
        const folders = ref([])
        const activeCoachTab = ref('main')
        const showAddForm = ref(false)
        const newName = ref('')
        const renamingId = ref(null)
        const renameValue = ref('')

        const canEdit = computed(() => localStorage.getItem('userType') !== 'supervisor')

        const loadFolders = async () => {
            const response = await getAllTrainingElementFoldersWithSubfolders()
            folders.value = Array.isArray(response) ? response : []
        }

        const visibleTiles = computed(() => {
            return folders.value
                .filter((entry) => (entry.folder?.coach_type || 'main') === activeCoachTab.value)
                .map((entry) => ({
                    ...entry.folder,
                    subfolders: entry.subfolders || [],
                    _kind: 'folder',
                    _entry: entry
                }))
        })

        const onTileClick = (item) => {
            if (renamingId.value) return

            emit('select', {
                id: item.id,
                name: item.name,
                coach_type: item.coach_type,
                subfolders: item.subfolders || []
            })
        }

        const startRename = (item) => {
            renamingId.value = item.id
            renameValue.value = item.name
        }

        const cancelRename = () => {
            renamingId.value = null
            renameValue.value = ''
        }

        const submitRename = async (item) => {
            const name = renameValue.value.trim()
            if (!name) return

            await updateTrainingElementFolderById(item.id, name, item.coach_type)
            cancelRename()
            await loadFolders()
        }

        const confirmDelete = async (item) => {
            if (!confirm(`Видалити папку «${item.name}»?`)) return
            await deleteTrainingElementFolderById(item.id)
            await loadFolders()
        }

        const cancelAdd = () => {
            showAddForm.value = false
            newName.value = ''
        }

        const submitAdd = async () => {
            const name = newName.value.trim()
            if (!name) return

            await addTrainingElementFolder(name, uuid.v4(), activeCoachTab.value)
            cancelAdd()
            await loadFolders()
        }

        watch(activeCoachTab, () => {
            cancelAdd()
            cancelRename()
        })

        onMounted(loadFolders)

        return {
            folders,
            activeCoachTab,
            visibleTiles,
            showAddForm,
            newName,
            renamingId,
            renameValue,
            canEdit,
            onTileClick,
            startRename,
            cancelRename,
            submitRename,
            confirmDelete,
            cancelAdd,
            submitAdd,
            loadFolders
        }
    }
}
</script>
